import { Filter, FilterGroup } from '@kentik/ui-shared/query/query.types';
import { Variable } from '@kentik/ui-shared/boards/boards';
import { VariableMapping } from '@kentik/ui-shared/boards/panels';

type ResolvedSingle = { multi: false; filterField: string; value: string; operator: string };
type ResolvedMulti = { multi: true; filterField: string; value: string[]; operator: string };
type ResolvedVariable = ResolvedSingle | ResolvedMulti;

function makeFilter(filterField: string, filterValue: string, operator: string): Filter {
  return {
    filterField,
    operator,
    filterValue,
    rightFilterField: ''
  };
}

/**
 * Optional resolver for guided variable values.
 * Receives the target filterField and raw variable value (always a name/label);
 * returns the value the filter should actually use — e.g. numeric ID for `i_device_id`.
 */
export type GuidedValueResolver = (filterField: string, value: string) => string;

/**
 * Converts board config variables to query filter format.
 * Uses panel variable mappings to resolve each variable name to a filter field.
 * Multi-value variables are placed in a nested Any group so their values are OR'd together.
 * Different variables are AND'd via the outer All group.
 *
 * @param variables - Array of board variables from config.variables
 * @param mappings - Panel variable mappings from config.variableMappings
 * @param resolveGuidedValue - Optional resolver for guided variable values (e.g. name → ID)
 */
export function convertBoardVariablesToFilters(
  variables: Variable[],
  mappings: Record<string, VariableMapping>,
  resolveGuidedValue?: GuidedValueResolver
): FilterGroup {
  const resolved = variables.flatMap<ResolvedVariable>((variable) => {
    const mapping = mappings[variable.name];
    if (!mapping?.filterField) {
      return [];
    }

    const { filterField } = mapping;
    const { value, allowMulti = false, exactMatch = false, type, operator: variableOperator } = variable;
    const operator = variableOperator || mapping.operator || (exactMatch ? '=' : 'ILIKE');

    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      return [];
    }

    const resolve = (v: string) => (type === 'guided' && resolveGuidedValue ? resolveGuidedValue(filterField, v) : v);

    if (allowMulti && Array.isArray(value) && value.length > 1) {
      return [{ multi: true, filterField, value: value.map(resolve), operator }];
    }

    const singleValue = Array.isArray(value) ? value[0] : value;
    return [{ multi: false, filterField, value: resolve(singleValue), operator }];
  });

  const filters: Filter[] = resolved
    .filter((r): r is ResolvedSingle => !r.multi)
    .map((r) => makeFilter(r.filterField, r.value, r.operator));

  const filterGroups: FilterGroup[] = resolved
    .filter((r): r is ResolvedMulti => r.multi)
    .map((r) => ({
      connector: 'Any' as const,
      not: false,
      filters: r.value.map((v) => makeFilter(r.filterField, v, r.operator)),
      filterGroups: []
    }));

  return { connector: 'All', not: false, filters, filterGroups };
}
